import axios from 'axios';
import { Storage } from 'react-jhipster';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { AppThunk } from '../../../../../app/config/store';
import { setLocale } from '../../../app/shared/reducers/locale';
import { serializeAxiosError } from './reducer.utils';

export const initialState = {
  loading: false,
  isAuthenticated: false,
  account: {} as any,
  errorMessage: null as unknown as string, // Errors returned from server side
  redirectMessage: null as unknown as string,
  sessionHasBeenFetched: false,
  logoutUrl: null as unknown as string,
};

export type AuthenticationState = Readonly<typeof initialState>;

// Actions

export const startSessionWatcher = (): AppThunk => (dispatch, getState) => {
  let idleTimeout: any;
  const checkSession = async () => {
    try {
      await dispatch(getSession());
      const { account } = getState().authentication;
      if (!account) {
        await dispatch(logout());
      }
    } catch (err) {
      console.warn('Session expired or network error', err);
      await dispatch(logout());
    }
  };

  const resetIdleTimer = () => {
    clearTimeout(idleTimeout);
    idleTimeout = setTimeout(
      () => {
        console.log('User idle, checking session...');
        checkSession();
      },
      1 * 60 * 1000,
    );
  };

  const events = ['mousemove', 'keydown', 'scroll', 'click'];
  events.forEach(e => window.addEventListener(e, resetIdleTimer));

  const handleVisibility = () => {
    if (!document.hidden) {
      console.log('User returned to tab, checking session...');
      checkSession();
    }
  };
  document.addEventListener('visibilitychange', handleVisibility);

  resetIdleTimer();

  return () => {
    clearTimeout(idleTimeout);
    events.forEach(e => window.removeEventListener(e, resetIdleTimer));
    document.removeEventListener('visibilitychange', handleVisibility);
  };
};

export const getSession = (): AppThunk => async (dispatch, getState) => {
  await dispatch(getAccount());

  const { account } = getState().authentication;
  if (account && account.langKey) {
    const langKey = Storage.session.get('locale', account.langKey);
    await dispatch(setLocale(langKey));
  }
};

export const getAccount = createAsyncThunk('authentication/get_account', async () => axios.get<any>('api/account'), {
  serializeError: serializeAxiosError,
});

export const logoutServer = createAsyncThunk(
  'authentication/logout',
  async () => {
    const response = await axios.post<any>('api/logout', {});
    return response;
  },
  {
    serializeError: serializeAxiosError,
  },
);

export const logout: () => AppThunk = () => async dispatch => {
  await dispatch(logoutServer());
  // fetch new csrf token
  dispatch(getSession());
};

export const clearAuthentication = messageKey => dispatch => {
  dispatch(authError(messageKey));
  dispatch(clearAuth());
};

export const AuthenticationSlice = createSlice({
  name: 'authentication',
  initialState: initialState as AuthenticationState,
  reducers: {
    authError(state, action) {
      return {
        ...state,
        redirectMessage: action.payload,
      };
    },
    clearAuth(state) {
      return {
        ...state,
        loading: false,
        isAuthenticated: false,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getAccount.rejected, (state, action) => ({
        ...state,
        loading: false,
        isAuthenticated: false,
        sessionHasBeenFetched: true,
        errorMessage: action.error.message ?? 'Unknown error occurred',
      }))
      .addCase(getAccount.fulfilled, (state, action) => {
        const isAuthenticated = action.payload && action.payload.data && action.payload.data.activated;
        localStorage.setItem('isActive', JSON.stringify(isAuthenticated));
        // if (isAuthenticated) {
        //   Storage.session.set('userSession', JSON.stringify(action.payload.data));
        // }
        return {
          ...state,
          isAuthenticated,
          loading: false,
          sessionHasBeenFetched: true,
          account: action.payload.data,
        };
      })
      .addCase(logoutServer.fulfilled, (state, action) => ({
        ...initialState,
        logoutUrl: action.payload.data.logoutUrl,
      }))
      .addCase(getAccount.pending, state => {
        state.loading = true;
      });
  },
});

export const { authError, clearAuth } = AuthenticationSlice.actions;

// Reducer
export default AuthenticationSlice.reducer;
